import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useLiveTvEnabled } from '@/hooks/useLiveTvEnabled';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useRecruiterEnabled } from '@/hooks/useRecruiterEnabled';
import { useAppDispatch, useAppSelector } from '@/store';
import { logout } from '@/store/slices/authSlice';
import { createMyLobby, joinMyLobby, leaveMyLobby } from '@/store/slices/lobbySlice';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from './SearchIcon';
import { ThemeSelector } from '@/theme/ThemeSelector';
import { LanguageSelector } from './LanguageSelector';
import { NavDrawer } from './NavDrawer';

interface Props {
  /** True when the user is in a lobby with a current pick they aren't watching. */
  showJoinLobbyButton?: boolean;
  /** Navigate to the lobby's current `/watch/` URL. */
  onJoinLobby?: () => void;
}

export function AppNav({ showJoinLobbyButton = false, onJoinLobby }: Props = {}): JSX.Element {
  const { status, user, admin, access } = useAuth();
  const lobby = useAppSelector((s) => s.lobby);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isHome = location.pathname === '/';
  const showSearchIcon = !isHome;

  // Context-aware search: inside a Sports section the search icon jumps to that
  // section's own search page — the replay library (/sports-vod) or the live
  // events page (/sports), both of which host their own search box — instead of
  // the movie/TV results page. Check sports-vod first: '/watch/sports' would
  // otherwise also match '/watch/sports-vod'.
  const searchTarget =
    location.pathname.startsWith('/sports-vod') || location.pathname.startsWith('/watch/sports-vod')
      ? '/sports-vod'
      : location.pathname === '/sports'
        || location.pathname.startsWith('/sports/')
        || location.pathname.startsWith('/watch/sports')
        ? '/sports'
        : '/results';

  // Live TV is dark-shipped: the link only appears once the backend confirms
  // the section is enabled (a 200 from /api/livetv/enabled). Probed only when
  // the user has access (or is an admin) so guests don't fire a doomed request.
  const liveTvEnabled = useLiveTvEnabled(access || admin);

  // Recruitment is dark-shipped like Live TV: the tab appears only for a user
  // whose billing snapshot reports isRecruiter AND once the backend confirms
  // the platform is enabled (probe gated on access/admin so guests don't fire).
  // autofetch:false — the nav only READS the snapshot (fetched elsewhere); a
  // fetch here would fire an extra /api/billing/me that pollutes unrelated
  // fetch-call assertions and can consume a shared mocked Response body.
  const { isRecruiter } = useEntitlements({ autofetch: false });
  const recruiterEnabled = useRecruiterEnabled(access || admin);

  // `isRealUser` gates Sign in / Sign out + display name in the desktop nav and
  // the drawer header. Lobby controls use only `requireAccess` on the backend
  // (see cobstream-backend/src/middleware/requireFullUser.ts: "Lobby endpoints
  // should NOT use this — they permit guests on purpose."), so guests with a
  // redeemed access code can create/join/leave like full users.
  const isRealUser = status === 'authenticated' && user != null && user.guest !== true;
  const canUseLobby = status === 'authenticated' && access;
  const inLobby = lobby.currentLobby != null;
  const lobbyBusy = lobby.status === 'loading';

  async function onCreate(): Promise<void> {
    await dispatch(createMyLobby());
  }

  async function onLeave(): Promise<void> {
    await dispatch(leaveMyLobby());
  }

  async function onJoinSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const code = joinCode.trim();
    if (!code) return;
    const result = await dispatch(joinMyLobby(code));
    if (joinMyLobby.fulfilled.match(result)) {
      setJoinCode('');
      setShowJoinForm(false);
    }
  }

  async function onJoinFromDrawer(code: string): Promise<void> {
    await dispatch(joinMyLobby(code));
  }

  function onSignOut(): void {
    dispatch(logout());
    navigate('/');
  }

  return (
    <nav className="app-nav">
      <Link to="/" className="app-nav__brand" aria-label={t('nav.brandHome')}>
        COBSTREAM
      </Link>
      <div className="app-nav__actions">
        {showSearchIcon && (
          <button
            type="button"
            className="icon-btn"
            aria-label={t('nav.searchAria')}
            onClick={() => {
              if (location.pathname !== searchTarget) navigate(searchTarget);
            }}
          >
            <SearchIcon />
          </button>
        )}

        <ThemeSelector />
        <LanguageSelector />

        {/* Desktop nav items — hidden below --bp-lg (960px) via CSS. */}
        <div className="app-nav__actions--desktop">
          {canUseLobby && (
            inLobby ? (
              <div className="lobby-nav" aria-label={t('nav.lobbyControls')}>
                <span className="lobby-nav__code" aria-label={t('nav.lobbyCode')}>
                  {lobby.currentLobby!.code}
                </span>
                {showJoinLobbyButton && onJoinLobby && (
                  <button
                    type="button"
                    className="btn"
                    onClick={onJoinLobby}
                  >
                    {t('nav.joinLobbyCta')}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={onLeave}
                  disabled={lobbyBusy}
                >
                  {t('nav.leaveLobby')}
                </button>
              </div>
            ) : (
              <div className="lobby-nav" aria-label={t('nav.lobbyControls')}>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={onCreate}
                  disabled={lobbyBusy}
                >
                  {t('nav.createLobby')}
                </button>
                {showJoinForm ? (
                  <form className="lobby-nav__join" onSubmit={onJoinSubmit}>
                    <input
                      type="text"
                      aria-label={t('nav.lobbyCode')}
                      placeholder={t('nav.lobbyCodePlaceholder')}
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                      maxLength={32}
                      autoFocus
                    />
                    <button type="submit" className="btn-ghost" disabled={lobbyBusy || !joinCode.trim()}>
                      {t('nav.join')}
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => { setShowJoinForm(false); setJoinCode(''); }}
                    >
                      {t('common.cancel')}
                    </button>
                  </form>
                ) : (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setShowJoinForm(true)}
                    disabled={lobbyBusy}
                  >
                    {t('nav.joinLobby')}
                  </button>
                )}
              </div>
            )
          )}

          {/* Admins always see Sports nav even without the `access` claim —
              Jacob's admin token doesn't carry access:true and was hidden
              from the links he literally built. */}
          {(access || admin) && (
            <Link to="/sports" className="btn-ghost">
              {t('nav.sports')}
            </Link>
          )}

          {(access || admin) && (
            <Link to="/sports-vod" className="btn-ghost">
              {t('nav.sportsVod')}
            </Link>
          )}

          {(access || admin) && liveTvEnabled && (
            <Link to="/live-tv" className="btn-ghost">
              {t('nav.liveTv')}
            </Link>
          )}

          <Link to="/account" className="btn-ghost">
            {t('nav.account')}
          </Link>

          {isRecruiter && recruiterEnabled && (
            <Link to="/recruitment/earnings" className="btn-ghost">
              {t('recruitment.nav.label')}
            </Link>
          )}

          {admin && (
            <Link to="/admin/access-codes" className="btn-ghost">
              {t('nav.admin')}
            </Link>
          )}

          {status === 'authenticated' && user && user.guest !== true ? (
            <>
              <span className="nav-user" aria-label={t('nav.signedInUser')}>
                {user.displayName ?? user.email}
              </span>
              <button
                type="button"
                className="btn btn-outline"
                onClick={onSignOut}
              >
                {t('nav.signOut')}
              </button>
            </>
          ) : (
            <>
              <Link to="/register" className="btn-ghost">
                {t('nav.createAccount')}
              </Link>
              <Link to="/login" className="btn">
                {t('nav.signIn')}
              </Link>
            </>
          )}
        </div>

        {/* Hamburger — visible at <= 960px via CSS. */}
        <button
          type="button"
          className="hamburger-btn icon-btn"
          aria-label={t('nav.menu')}
          aria-expanded={drawerOpen}
          aria-controls="nav-drawer"
          onClick={() => setDrawerOpen(true)}
        >
          {/* Three-line hamburger glyph */}
          <svg
            width="20"
            height="14"
            viewBox="0 0 20 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="1" y1="2" x2="19" y2="2" />
            <line x1="1" y1="7" x2="19" y2="7" />
            <line x1="1" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>

      <NavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isAuthenticated={isRealUser}
        displayName={isRealUser ? (user?.displayName ?? user?.email ?? null) : null}
        admin={admin}
        access={access}
        canUseLobby={canUseLobby}
        inLobby={inLobby}
        lobbyBusy={lobbyBusy}
        lobbyCode={inLobby ? lobby.currentLobby!.code : null}
        showJoinLobbyButton={showJoinLobbyButton}
        onJoinLobby={onJoinLobby}
        onCreate={onCreate}
        onLeave={onLeave}
        onJoin={onJoinFromDrawer}
        onSignOut={onSignOut}
      />
    </nav>
  );
}
